/**
 * Home.
 *
 * Directive §11 preserves: greeting, current date, Today's visits, team count,
 * physical stop count, schedule warning, Open Today's Plan, follow-up summary,
 * oldest follow-up, upcoming routes.
 *
 * Every count comes from server state. §11 forbids hard-coding the screenshot
 * fixture values, so this component takes them as props and renders nothing
 * until they arrive.
 */

import type { ScreenId } from '../components/AppShell.js';
import { CalendarDaysIcon, MapIcon, PhoneIcon } from '@heroicons/react/24/outline';

export interface UpcomingRoute {
  id: string;
  teamName: string;
  stopCount: number;
  miles: number;
  startTime: string;
}

export interface HomeData {
  firstName: string;
  /** Server-formatted so the greeting matches the operator's timezone. */
  todayLabel: string;
  greeting: string;
  visitCount: number;
  teamCount: number;
  stopCount: number;
  /** Null when the day has no conflicts. §11 */
  scheduleWarning: string | null;
  followUpCount: number;
  oldestFollowUpLabel: string | null;
  upcoming: UpcomingRoute[];
}

export function HomeScreen({
  data, onNavigate, onOpenTodaysPlan, onViewFollowUps, onOpenRoute,
}: {
  data: HomeData | null;
  onNavigate: (id: ScreenId) => void;
  onOpenTodaysPlan: () => void;
  onViewFollowUps: () => void;
  onOpenRoute: (routeId: string) => void;
}) {
  if (!data) return <HomeSkeleton />;

  return (
    <div className="pt-4 desktop:pt-6 flex flex-col gap-8">
      <p className="text-base text-neutral-700">{data.todayLabel}</p>
      <h1 className="mt-[-36px] pt-6">
        {data.greeting}, {data.firstName}
      </h1>

      <div className="grid grid-cols-1 desktop:grid-cols-2 gap-8">
        <section aria-labelledby="today-heading">
          <h6 id="today-heading" className="text-accent-700 mb-2 flex items-center gap-1"><CalendarDaysIcon className="w-4 h-4" />Today</h6>

          <p className="font-heading font-semibold text-h2">
            {data.visitCount} {data.visitCount === 1 ? 'visit' : 'visits'}
          </p>

          <p className="mt-1 text-base text-neutral-700">
            {data.teamCount} {data.teamCount === 1 ? 'team' : 'teams'}
            {' · '}
            {data.stopCount} {data.stopCount === 1 ? 'stop' : 'stops'}
          </p>

          {/* Magenta is reserved for exceptions — this is one. §7 */}
          {data.scheduleWarning && (
            <p className="mt-1 text-base text-alert-600">{data.scheduleWarning}</p>
          )}

          <button
            className="btn btn-primary mt-4"
            onClick={onOpenTodaysPlan}
            disabled={data.visitCount === 0}
          >
            Open Today's Plan
          </button>
        </section>

        <section aria-labelledby="followup-heading">
          <h6 id="followup-heading" className="text-accent-700 mb-2 flex items-center gap-1"><PhoneIcon className="w-4 h-4" />Follow up</h6>

          <p className="font-heading font-semibold text-h2">
            {data.followUpCount} {data.followUpCount === 1 ? 'call' : 'calls'} needed
          </p>

          {data.oldestFollowUpLabel && (
            <p className="mt-1 text-base text-neutral-700">
              Oldest due {data.oldestFollowUpLabel}
            </p>
          )}

          <button className="btn btn-secondary mt-4" onClick={onViewFollowUps}>
            View Follow-Ups
          </button>
        </section>
      </div>

      <section aria-labelledby="upcoming-heading">
        <h6 id="upcoming-heading" className="text-accent-700 mb-2 flex items-center gap-1"><MapIcon className="w-4 h-4" />Upcoming</h6>

        {data.upcoming.length === 0 ? (
          <p className="text-base text-neutral-700">No routes scheduled.</p>
        ) : (
          <ul>
            {data.upcoming.map((r) => (
              <li key={r.id} className="border-b border-neutral-300 last:border-0">
                <button
                  onClick={() => onOpenRoute(r.id)}
                  className="w-full min-h-touch py-3 flex items-baseline gap-2 text-left
                             hover:bg-neutral-200 transition-colors"
                >
                  <span className="font-heading font-semibold text-h4">{r.teamName}</span>
                  <span className="text-base text-neutral-700">
                    · {r.stopCount} stops · {r.miles} mi
                  </span>
                  <span className="ml-auto text-base tabular-nums">{r.startTime}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Loading state.
 *
 * Deliberately shows no numbers — not even zeros. A zero that later becomes 37
 * is a misleading intermediate state on a screen an operator uses to decide
 * whether their day has work in it. §11
 */
function HomeSkeleton() {
  return (
    <div className="pt-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading today's plan</span>
      <div className="h-5 w-40 bg-neutral-300 rounded-sm" />
      <div className="h-10 w-72 bg-neutral-300 rounded-sm mt-3 mb-6" />
      <div className="grid grid-cols-1 desktop:grid-cols-2 gap-8">
        {[0, 1].map((i) => (
          <div key={i}>
            <div className="h-4 w-24 bg-neutral-300 rounded-sm mb-3" />
            <div className="h-8 w-40 bg-neutral-300 rounded-sm mb-2" />
            <div className="h-5 w-52 bg-neutral-300 rounded-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}
